// src/components/ProductLookup.tsx
// Общее для «Продажи» и «Приёмки» (17.10.2026). До этого обе страницы искали товар
// только по штрихкоду, а штрихкода не было ни у одной карточки — касса и приёмка
// не находили ничего, и остаток на сайте не менялся. Теперь ищут и по номеру /
// модели с коробки; здесь — выбор, когда нашлось несколько, и привязка
// отсканированного штрихкода к карточке, чтобы следующий скан находил её сразу.
import React, { useState } from 'react';
import { Alert, Button, List, Modal, Space, Tag, message } from 'antd';
import { apiLinkBarcode } from '../services/productAPI';

const CATEGORY_LABELS: Record<string, string> = {
  spark_plugs:               'Свечи зажигания',
  ignition_coils:            'Катушки зажигания',
  crankshaft_sensors:        'Датчики',
  camshaft_sensors:          'Датчики',
  airbag_cables:             'Рулевые шлейфы',
  ignition_coil_mouthpieces: 'Наконечники и провода',
};

/** Похоже на скан со сканера: только цифры, 8–14 знаков (EAN-8, UPC, EAN-13, ITF-14). */
export const looksLikeBarcode = (q: string) => /^\d{8,14}$/.test(q.trim());

/** Название товара для сообщений: марка + номер детали (или модель). */
export const productName = (p: any) =>
  `${p?.brand ?? ''} ${p?.partNumber ?? p?.detail_number ?? p?.model ?? ''}`.trim();

type PickerProps = {
  query: string;
  items: any[];
  onPick: (p: any) => void;
  onCancel: () => void;
};

/** Нашлось несколько карточек — выбрать нужную. */
export const ProductPickerModal: React.FC<PickerProps> = ({ query, items, onPick, onCancel }) => (
  <Modal
    open={items.length > 0}
    onCancel={onCancel}
    footer={null}
    title={`По «${query}» нашлось ${items.length} — выберите`}
    destroyOnHidden
  >
    <List
      dataSource={items}
      rowKey={(p: any) => String(p.id ?? p.product_id)}
      renderItem={(p: any) => (
        <List.Item
          style={{ cursor: 'pointer' }}
          onClick={() => onPick(p)}
          actions={[
            <Button key="pick" type="primary" size="small" onClick={(e) => { e.stopPropagation(); onPick(p); }}>
              Выбрать
            </Button>,
          ]}
        >
          <List.Item.Meta
            title={
              <span>
                <strong>{productName(p)}</strong>
                {p.model && p.model !== p.partNumber && (
                  <span style={{ color: '#8c8c8c', marginLeft: 6 }}>/ {p.model}</span>
                )}
              </span>
            }
            description={
              <Space size={4} wrap>
                <Tag color="blue">{CATEGORY_LABELS[p.category_name] ?? p.category_name}</Tag>
                <Tag>оригинал: {Number(p.count_original) || 0} шт.</Tag>
                <Tag>копия: {Number(p.count_copy) || 0} шт.</Tag>
                {p.sku && <Tag>штрихкод {p.sku}</Tag>}
              </Space>
            }
          />
        </List.Item>
      )}
    />
  </Modal>
);

type LinkProps = {
  barcode: string | null;
  product: any | null;
  /** Закрыть вопрос — и после привязки, и после «Не надо». */
  onDone: () => void;
  /** Только после удачной привязки: штрихкод и товар. */
  onLinked?: (barcode: string, product: any) => void;
};

/** После выбора товара по номеру: привязать к нему штрихкод, который до этого не нашёлся. */
export const BarcodeLinkPrompt: React.FC<LinkProps> = ({ barcode, product, onDone, onLinked }) => {
  const [saving, setSaving] = useState(false);
  if (!barcode || !product) return null;
  const name = productName(product);
  const link = async () => {
    setSaving(true);
    try {
      await apiLinkBarcode(product.id ?? product.product_id, barcode);
      message.success(`Штрихкод ${barcode} привязан к ${name} — дальше скан найдёт его сразу`);
      onLinked?.(barcode, product);
      onDone();
    } catch (e: any) {
      message.error(e?.message || 'Не удалось привязать штрихкод');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Alert
      style={{ marginBottom: 16 }}
      type="info"
      showIcon
      message={`Привязать штрихкод ${barcode} к ${name}?`}
      description="Следующий скан этой коробки сразу найдёт товар."
      action={
        <Space>
          <Button size="small" type="primary" loading={saving} onClick={link}>Привязать</Button>
          <Button size="small" onClick={onDone}>Не надо</Button>
        </Space>
      }
    />
  );
};
